import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTeam } from '../contexts/TeamContext';
import Layout from '../components/Layout';
import { API_URL } from '../services/api';
import { useApiHeaders } from '../hooks/useApiHeaders';
import LicenseExpiredBanner from '../components/LicenseExpiredBanner';
import PolicyConfigTabs, {
	type PolicyConfig,
	type AIProviderName,
	type ChannelKey,
	type TeamAgent,
	type TeamAgentInstance,
	PROVIDER_MODELS,
	DEFAULT_INTEGRATIONS,
	DEFAULT_OAUTH,
	DEFAULT_AI_PROVIDERS,
} from '../components/PolicyConfigTabs';
import SkillManager from '../components/SkillManager';

export default function SettingsPage() {
	const { firebaseUser } = useAuth();
	const { currentTeam, refreshTeams } = useTeam();
	const { getHeaders } = useApiHeaders();
	const [loading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);

	// Team settings state
	const [teamName, setTeamName] = useState('');
	const [isOwner, setIsOwner] = useState(false);

	// Team default policy state
	const [teamPolicy, setTeamPolicy] = useState<PolicyConfig | null>(null);
	const [teamPolicyLoading, setTeamPolicyLoading] = useState(false);
	const [teamPolicySaving, setTeamPolicySaving] = useState(false);
	const [policyStatus, setPolicyStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
	const [activeTab, setActiveTab] = useState<'general' | 'workspace' | 'skills'>('general');

	// Credential modals (for AI provider setup in team defaults)
	const [showApiKeyModal, setShowApiKeyModal] = useState<'openai' | 'google_ai' | 'openrouter' | null>(null);
	const [showClaudeTokenModal, setShowClaudeTokenModal] = useState(false);
	const [apiKeyInput, setApiKeyInput] = useState('');
	const [claudeTokenInput, setClaudeTokenInput] = useState('');
	const [savingCredential, setSavingCredential] = useState(false);

	// Channel Keys state
	// ChannelKey type imported from PolicyConfigTabs; extend locally for extra fields
	type ChannelKeyLocal = ChannelKey & {
		assigned_agents: number;
		created_at: number;
		updated_at: number;
	};
	type PoolStatusEntry = {
		channel_type: string;
		total: number;
		assigned: number;
		available: number;
	};
	const [channelKeys, setChannelKeys] = useState<ChannelKeyLocal[]>([]);
	const [, setPoolStatus] = useState<PoolStatusEntry[]>([]);
	const [, setChannelKeysLoading] = useState(false);
	const [showChannelKeyModal, setShowChannelKeyModal] = useState(false);
	const [editingKeyId, setEditingKeyId] = useState<string | null>(null);
	const [channelKeyForm, setChannelKeyForm] = useState({
		channel_type: 'telegram' as string,
		label: '',
		is_default: false,
		config: {} as Record<string, string>,
	});
	const [savingChannelKey, setSavingChannelKey] = useState(false);
	const [channelKeyStatus, setChannelKeyStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

	// Team agents state (for channel key assignment dropdown)
	const [teamAgents, setTeamAgents] = useState<TeamAgent[]>([]);

	// Channel key config field definitions per type
	const CHANNEL_CONFIG_FIELDS: Record<string, { key: string; label: string; placeholder: string; required?: boolean }[]> = {
		telegram: [
			{ key: 'bot_token', label: 'Bot Token', placeholder: '123456:ABC-DEF...', required: true },
		],
		slack: [
			{ key: 'bot_token', label: 'Bot Token', placeholder: 'xoxb-...', required: true },
			{ key: 'app_token', label: 'App Token', placeholder: 'xapp-...' },
			{ key: 'signing_secret', label: 'Signing Secret', placeholder: 'Signing secret...', required: true },
		],
		discord: [
			{ key: 'bot_token', label: 'Bot Token', placeholder: 'Bot token...', required: true },
			{ key: 'application_id', label: 'Application ID', placeholder: 'Application ID...', required: true },
		],
		wechat: [
			{ key: 'app_id', label: 'App ID', placeholder: 'wx1234567890abcdef', required: true },
			{ key: 'app_secret', label: 'App Secret', placeholder: 'App secret...', required: true },
			{ key: 'token', label: 'Token', placeholder: 'Verification token...', required: true },
			{ key: 'encoding_aes_key', label: 'EncodingAESKey', placeholder: 'Message encryption key (optional)' },
		],
		whatsapp: [
			{ key: 'phone_number_id', label: 'Phone Number ID', placeholder: '1234567890...', required: true },
			{ key: 'access_token', label: 'Access Token', placeholder: 'Access token...', required: true },
			{ key: 'business_account_id', label: 'Business Account ID', placeholder: 'Business account ID...', required: true },
			{ key: 'verify_token', label: 'Verify Token', placeholder: 'Webhook verify token (optional)' },
		],
		imessage: [
			{ key: 'business_id', label: 'Business ID', placeholder: 'Business ID...', required: true },
			{ key: 'msp_id', label: 'MSP ID', placeholder: 'Messaging Service Provider ID...', required: true },
			{ key: 'api_key', label: 'API Key', placeholder: 'API key...', required: true },
		],
		webhook: [
			{ key: 'secret_key', label: 'Secret Key', placeholder: 'Secret key...', required: true },
		],
		email: [
			{ key: 'password', label: 'Password', placeholder: 'Password...', required: true },
		],
	};

	const CHANNEL_TYPE_LABELS: Record<string, string> = {
		telegram: 'Telegram',
		slack: 'Slack',
		discord: 'Discord',
		wechat: 'WeChat',
		whatsapp: 'WhatsApp',
		imessage: 'iMessage',
		webhook: 'Webhook',
		email: 'Email',
	};

	// Fetch channel keys and pool status
	const fetchChannelKeys = useCallback(async () => {
		if (!firebaseUser) return;
		setChannelKeysLoading(true);
		try {
			const headers = await getHeaders();
			const [keysResponse, poolResponse] = await Promise.all([
				fetch(`${API_URL}/api/v1/channel-keys`, { headers }),
				fetch(`${API_URL}/api/v1/channel-keys/pool-status`, { headers }),
			]);
			if (keysResponse.ok) {
				const data = await keysResponse.json();
				setChannelKeys(data.keys || []);
			}
			if (poolResponse.ok) {
				const data = await poolResponse.json();
				setPoolStatus(data.pool || []);
			}
		} catch (err) {
			console.error('Error fetching channel keys:', err);
		} finally {
			setChannelKeysLoading(false);
		}
	}, [firebaseUser]);

	// Fetch team agents and instances for key assignment dropdown
	const fetchTeamAgents = useCallback(async () => {
		if (!firebaseUser) return;
		try {
			const headers = await getHeaders();
			const [agentsResponse, instancesResponse] = await Promise.all([
				fetch(`${API_URL}/api/v1/agents`, { headers }),
				fetch(`${API_URL}/api/v1/instances`, { headers }),
			]);
			let agents: TeamAgent[] = [];
			let instances: (TeamAgentInstance & { agent_id: string })[] = [];
			if (agentsResponse.ok) {
				const data = await agentsResponse.json();
				agents = (data.agents || []).map((a: any) => ({
					agent_id: a.agent_id,
					hostname: a.hostname,
					status: a.status,
				}));
			}
			if (instancesResponse.ok) {
				const data = await instancesResponse.json();
				instances = (data.instances || []).map((i: any) => ({
					instance_id: i.instance_id,
					instance_name: i.instance_name,
					agent_id: i.agent_id,
					running_status: i.running_status,
				}));
			}
			// Attach instances to their parent agents
			const agentsWithInstances = agents.map(agent => ({
				...agent,
				instances: instances
					.filter(inst => inst.agent_id === agent.agent_id)
					.map(({ agent_id: _, ...rest }) => rest),
			}));
			setTeamAgents(agentsWithInstances);
		} catch (err) {
			console.error('Error fetching team agents:', err);
		}
	}, [firebaseUser]);

	useEffect(() => {
		if (currentTeam) {
			fetchChannelKeys();
			fetchTeamAgents();
		}
	}, [currentTeam, fetchChannelKeys, fetchTeamAgents]);

	// Assign a channel key to a specific instance
	const handleAssignChannelKey = async (keyId: string, instanceId: string) => {
		if (!firebaseUser) return;
		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/channel-keys/${keyId}/assign/${instanceId}`, {
				method: 'PUT',
				headers,
			});
			if (response.ok) {
				setChannelKeyStatus({ type: 'success', message: 'Key assignment updated.' });
				setTimeout(() => setChannelKeyStatus(null), 5000);
				await fetchChannelKeys();
			} else {
				const errorData = await response.json().catch(() => ({}));
				setChannelKeyStatus({ type: 'error', message: errorData.error || 'Failed to assign channel key' });
			}
		} catch (err) {
			setChannelKeyStatus({ type: 'error', message: 'Network error assigning channel key' });
		}
	};

	const handleSaveChannelKey = async () => {
		if (!firebaseUser) return;
		setSavingChannelKey(true);
		setChannelKeyStatus(null);
		try {
			const headers = await getHeaders();
			const url = editingKeyId
				? `${API_URL}/api/v1/channel-keys/${editingKeyId}`
				: `${API_URL}/api/v1/channel-keys`;
			const method = editingKeyId ? 'PUT' : 'POST';

			const body: any = {};
			if (!editingKeyId) {
				body.channel_type = channelKeyForm.channel_type;
			}
			body.label = channelKeyForm.label;
			body.is_default = channelKeyForm.is_default;
			// Only send config if any field is non-empty (for edits, allows updating label/default without re-entering secrets)
			const hasConfig = Object.values(channelKeyForm.config).some(v => v.trim() !== '');
			if (hasConfig) {
				body.config = channelKeyForm.config;
			} else if (!editingKeyId) {
				// New key requires config
				body.config = channelKeyForm.config;
			}

			const response = await fetch(url, {
				method,
				headers,
				body: JSON.stringify(body),
			});

			if (response.ok) {
				setShowChannelKeyModal(false);
				setEditingKeyId(null);
				setChannelKeyStatus({ type: 'success', message: editingKeyId ? 'Channel key updated.' : 'Channel key created.' });
				setTimeout(() => setChannelKeyStatus(null), 5000);
				await fetchChannelKeys();
			} else {
				const errorData = await response.json().catch(() => ({}));
				setChannelKeyStatus({ type: 'error', message: errorData.error || 'Failed to save channel key' });
			}
		} catch (err) {
			console.error('Error saving channel key:', err);
			setChannelKeyStatus({ type: 'error', message: 'Network error - please try again' });
		} finally {
			setSavingChannelKey(false);
		}
	};

	const handleDeleteChannelKey = async (keyId: string) => {
		if (!firebaseUser) return;
		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/channel-keys/${keyId}`, {
				method: 'DELETE',
				headers,
			});
			if (response.ok) {
				setChannelKeyStatus({ type: 'success', message: 'Channel key deleted.' });
				setTimeout(() => setChannelKeyStatus(null), 5000);
				await fetchChannelKeys();
			} else {
				const errorData = await response.json().catch(() => ({}));
				setChannelKeyStatus({ type: 'error', message: errorData.error || 'Failed to delete channel key' });
			}
		} catch (err) {
			console.error('Error deleting channel key:', err);
			setChannelKeyStatus({ type: 'error', message: 'Network error - please try again' });
		}
	};

	const openEditKeyModal = (key: ChannelKey) => {
		setEditingKeyId(key.key_id);
		setChannelKeyForm({
			channel_type: key.channel_type,
			label: key.label,
			is_default: key.is_default,
			config: {}, // Don't pre-fill secrets
		});
		setShowChannelKeyModal(true);
	};

	// Load current team data
	useEffect(() => {
		if (currentTeam) {
			setTeamName(currentTeam.team_name);
			setIsOwner(currentTeam.role === 'owner');
		}
	}, [currentTeam]);

	// Fetch team credential status and merge into ai_providers config
	const fetchTeamCredentials = useCallback(async (aiProviders: any) => {
		try {
			const credHeaders = await getHeaders();
			const credResponse = await fetch(`${API_URL}/api/v1/credentials/team`, {
				headers: credHeaders,
			});
			if (credResponse.ok) {
				const credData = await credResponse.json();
				const creds = credData.credentials || {};
				// Merge credential status into ai_providers
				const merged = { ...aiProviders };
				for (const provider of ['anthropic', 'openai', 'google_ai', 'openrouter'] as const) {
					if (creds[provider]?.is_configured) {
						merged[provider] = {
							...merged[provider],
							is_configured: true,
						};
					}
				}
				return merged;
			}
		} catch (err) {
			console.error('Error fetching team credentials:', err);
		}
		return aiProviders;
	}, []);

	// Fetch team default policy
	const fetchTeamPolicy = useCallback(async () => {
		if (!firebaseUser) return;

		setTeamPolicyLoading(true);
		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/policies/team-default`, {
				headers: headers,
			});

			if (response.ok) {
				const data = await response.json();
				// Merge with defaults for backward compatibility
				let aiProviders = {
					...DEFAULT_AI_PROVIDERS,
					...(data.config.ai_providers || {}),
					anthropic: { ...DEFAULT_AI_PROVIDERS.anthropic, ...(data.config.ai_providers?.anthropic || {}) },
					openai: { ...DEFAULT_AI_PROVIDERS.openai, ...(data.config.ai_providers?.openai || {}) },
					google_ai: { ...DEFAULT_AI_PROVIDERS.google_ai, ...(data.config.ai_providers?.google_ai || {}) },
					openrouter: { ...DEFAULT_AI_PROVIDERS.openrouter, ...(data.config.ai_providers?.openrouter || {}) },
				};

				// Fetch team credential status and merge is_configured flags
				aiProviders = await fetchTeamCredentials(aiProviders);

				const configWithDefaults: PolicyConfig = {
					...data.config,
					integrations: {
						...DEFAULT_INTEGRATIONS,
						...(data.config.integrations || {}),
						telegram: { ...DEFAULT_INTEGRATIONS.telegram, ...(data.config.integrations?.telegram || {}) },
						slack: { ...DEFAULT_INTEGRATIONS.slack, ...(data.config.integrations?.slack || {}) },
						discord: { ...DEFAULT_INTEGRATIONS.discord, ...(data.config.integrations?.discord || {}) },
						webhook: { ...DEFAULT_INTEGRATIONS.webhook, ...(data.config.integrations?.webhook || {}) },
						local_port: { ...DEFAULT_INTEGRATIONS.local_port, ...(data.config.integrations?.local_port || {}) },
						email: { ...DEFAULT_INTEGRATIONS.email, ...(data.config.integrations?.email || {}) },
						wechat: { ...DEFAULT_INTEGRATIONS.wechat, ...(data.config.integrations?.wechat || {}) },
						whatsapp: { ...DEFAULT_INTEGRATIONS.whatsapp, ...(data.config.integrations?.whatsapp || {}) },
						imessage: { ...DEFAULT_INTEGRATIONS.imessage, ...(data.config.integrations?.imessage || {}) },
					},
					oauth: {
						...DEFAULT_OAUTH,
						...(data.config.oauth || {}),
						google: { ...DEFAULT_OAUTH.google, ...(data.config.oauth?.google || {}) },
						github: { ...DEFAULT_OAUTH.github, ...(data.config.oauth?.github || {}) },
						microsoft: { ...DEFAULT_OAUTH.microsoft, ...(data.config.oauth?.microsoft || {}) },
					},
					ai_providers: aiProviders,
				};
				setTeamPolicy(configWithDefaults);
			}
		} catch (err) {
			console.error('Error fetching team policy:', err);
		} finally {
			setTeamPolicyLoading(false);
		}
	}, [firebaseUser, fetchTeamCredentials]);

	useEffect(() => {
		if (currentTeam) {
			fetchTeamPolicy();
		}
	}, [currentTeam, fetchTeamPolicy]);

	// Save team default policy
	const saveTeamPolicy = async () => {
		if (!firebaseUser || !teamPolicy) return;

		setTeamPolicySaving(true);
		setPolicyStatus(null);
		try {
			const headers = await getHeaders();
			// Strip is_configured from ai_providers before saving — it's derived from
			// credential status (separate table) and shouldn't be baked into policy data
			const policyToSave = { ...teamPolicy };
			if (policyToSave.ai_providers) {
				const cleanedProviders = { ...policyToSave.ai_providers };
				for (const key of Object.keys(cleanedProviders) as AIProviderName[]) {
					const { is_configured: _, ...rest } = cleanedProviders[key];
					cleanedProviders[key] = rest as typeof cleanedProviders[typeof key];
				}
				policyToSave.ai_providers = cleanedProviders;
			}
			const response = await fetch(`${API_URL}/api/v1/policies/team-default`, {
				method: 'PUT',
				headers: headers,
				body: JSON.stringify({ config: policyToSave }),
			});

			if (response.ok) {
				setPolicyStatus({ type: 'success', message: 'Team connector configurations saved. New workspaces will inherit these settings.' });
				setTimeout(() => setPolicyStatus(null), 5000);
			} else {
				const errorData = await response.json().catch(() => ({}));
				setPolicyStatus({ type: 'error', message: errorData.error || 'Failed to save connector configurations.' });
			}
		} catch (err) {
			console.error('Error saving team policy:', err);
			setPolicyStatus({ type: 'error', message: 'Network error. Please check your connection and try again.' });
		} finally {
			setTeamPolicySaving(false);
		}
	};

	const handlePolicyChange = (updates: Partial<PolicyConfig>) => {
		if (teamPolicy) {
			setTeamPolicy({ ...teamPolicy, ...updates });
		}
	};

	// Handler for connecting AI providers (opens credential modals)
	const handleConnectProvider = (provider: AIProviderName) => {
		if (provider === 'anthropic') {
			setShowClaudeTokenModal(true);
			setClaudeTokenInput('');
		} else {
			setShowApiKeyModal(provider);
			setApiKeyInput('');
		}
	};

	// Handler for removing AI provider credentials
	const handleRemoveProvider = async (provider: AIProviderName) => {
		if (!firebaseUser || !teamPolicy) return;

		try {
			const headers = await getHeaders();

			const response = await fetch(`${API_URL}/api/v1/credentials/team/${provider}`, {
				method: 'DELETE',
				headers: headers,
			});

			if (response.ok) {
				if (teamPolicy.ai_providers) {
					setTeamPolicy({
						...teamPolicy,
						ai_providers: {
							...teamPolicy.ai_providers,
							[provider]: {
								...teamPolicy.ai_providers[provider],
								enabled: false,
								is_configured: false,
								allowed_models: [],
							},
						},
					});
				}
				setPolicyStatus({ type: 'success', message: `${provider} credential removed.` });
				setTimeout(() => setPolicyStatus(null), 5000);
			} else {
				const errorData = await response.json().catch(() => ({}));
				setPolicyStatus({ type: 'error', message: errorData.error || 'Failed to remove credential' });
			}
		} catch (error) {
			console.error('Error removing credential:', error);
			setPolicyStatus({ type: 'error', message: 'Network error - please try again' });
		}
	};

	// Save Claude token (team-level)
	const handleSaveClaudeToken = async () => {
		if (!firebaseUser || !claudeTokenInput.trim()) return;

		setSavingCredential(true);
		setPolicyStatus(null);

		try {
			const headers = await getHeaders();

			const response = await fetch(`${API_URL}/api/v1/credentials/team`, {
				method: 'PUT',
				headers: headers,
				body: JSON.stringify({
					provider: 'anthropic',
					api_key: claudeTokenInput.trim(),
				}),
			});

			if (response.ok) {
				if (teamPolicy?.ai_providers) {
					const existingProvider = teamPolicy.ai_providers.anthropic;
					const defaultModels = !existingProvider.is_configured
						? PROVIDER_MODELS.anthropic.map(m => m.id)
						: existingProvider.allowed_models;
					setTeamPolicy({
						...teamPolicy,
						ai_providers: {
							...teamPolicy.ai_providers,
							anthropic: {
								...existingProvider,
								is_configured: true,
								enabled: existingProvider.is_configured ? existingProvider.enabled : true,
								allowed_models: defaultModels,
							},
						},
					});
				}
				setShowClaudeTokenModal(false);
				setClaudeTokenInput('');
				setPolicyStatus({ type: 'success', message: 'Claude token configured. Select models and click "Save Configurations" to apply.' });
				setTimeout(() => setPolicyStatus(null), 5000);
			} else {
				const errorData = await response.json().catch(() => ({}));
				setPolicyStatus({ type: 'error', message: errorData.error || 'Failed to save token' });
			}
		} catch (error) {
			console.error('Error saving credential:', error);
			setPolicyStatus({ type: 'error', message: 'Network error - please try again' });
		} finally {
			setSavingCredential(false);
		}
	};

	// Save API key for non-Anthropic providers (team-level)
	const handleSaveApiKey = async () => {
		if (!firebaseUser || !apiKeyInput.trim() || !showApiKeyModal) return;

		setSavingCredential(true);
		setPolicyStatus(null);

		try {
			const headers = await getHeaders();
			const providerKey = showApiKeyModal;

			const response = await fetch(`${API_URL}/api/v1/credentials/team`, {
				method: 'PUT',
				headers: headers,
				body: JSON.stringify({
					provider: providerKey,
					api_key: apiKeyInput.trim(),
				}),
			});

			if (response.ok) {
				if (teamPolicy?.ai_providers) {
					const existingProvider = teamPolicy.ai_providers[providerKey];
					const defaultModels = !existingProvider.is_configured
						? PROVIDER_MODELS[providerKey].map(m => m.id)
						: existingProvider.allowed_models;
					setTeamPolicy({
						...teamPolicy,
						ai_providers: {
							...teamPolicy.ai_providers,
							[providerKey]: {
								...existingProvider,
								is_configured: true,
								enabled: existingProvider.is_configured ? existingProvider.enabled : true,
								allowed_models: defaultModels,
							},
						},
					});
				}
				setShowApiKeyModal(null);
				setApiKeyInput('');
				setPolicyStatus({ type: 'success', message: 'API key configured. Select models and click "Save Configurations" to apply.' });
				setTimeout(() => setPolicyStatus(null), 5000);
			} else {
				const errorData = await response.json().catch(() => ({}));
				setPolicyStatus({ type: 'error', message: errorData.error || 'Failed to save API key' });
			}
		} catch (error) {
			console.error('Error saving credential:', error);
			setPolicyStatus({ type: 'error', message: 'Network error - please try again' });
		} finally {
			setSavingCredential(false);
		}
	};

	// Save team settings
	const saveTeamSettings = async () => {
		if (!firebaseUser || !currentTeam) {
			setError('No team selected');
			return;
		}

		if (!isOwner) {
			setError('Only team owners can update team settings');
			return;
		}

		if (!teamName || teamName.trim().length === 0) {
			setError('Team name is required');
			return;
		}

		setSaving(true);
		setError(null);
		setSuccess(null);

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/teams/${currentTeam.team_id}`, {
				method: 'PUT',
				headers: headers,
				body: JSON.stringify({
					team_name: teamName.trim(),
				}),
			});

			if (response.ok) {
				setSuccess('Team settings saved successfully!');
				await refreshTeams();
			} else {
				const errorData = await response.json().catch(() => ({ error: 'Failed to save team settings' }));
				setError(errorData.error || 'Failed to save team settings');
			}
		} catch (err) {
			console.error('Error saving team settings:', err);
			setError('Error saving team settings');
		} finally {
			setSaving(false);
		}
	};

	if (!currentTeam) {
		return (
			<>
				<LicenseExpiredBanner />
				<Layout title="Team Defaults" subtitle="Default configuration applied to all new workspaces">
					<div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-2xl p-6">
						<div className="flex items-start space-x-3">
							<svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
							</svg>
							<div>
								<p className="text-sm font-semibold text-yellow-900 dark:text-yellow-300 mb-1">No team selected</p>
								<p className="text-sm text-yellow-800 dark:text-yellow-400">Please select a team from the team selector to access settings.</p>
							</div>
						</div>
					</div>
				</Layout>
			</>
		);
	}

	return (
		<>
			<LicenseExpiredBanner />
			<Layout title="Team Defaults" subtitle="Default configuration applied to all new workspaces">
				<div className="space-y-6">
					{/* Tab Bar */}
					<div className="border-b border-slate-200 dark:border-slate-700">
						<nav className="-mb-px flex space-x-6">
							{(['general', 'workspace', 'skills'] as const).map((tab) => (
								<button
									key={tab}
									onClick={() => setActiveTab(tab)}
									className={`pb-3 px-1 border-b-2 text-sm font-medium transition-colors cursor-pointer ${
										activeTab === tab
											? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
											: 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600'
									}`}
								>
									{tab === 'general' ? 'General' : tab === 'workspace' ? 'Workspace' : 'Skills'}
								</button>
							))}
						</nav>
					</div>

					{activeTab === 'general' && (<>
					{/* Team Information */}
					<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-6">
						<div className="mb-6">
							<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Team Information</h3>
							<p className="text-sm text-slate-600 dark:text-slate-400">
								{isOwner
									? 'Manage your team name and settings'
									: 'View your team information (only owners can edit)'}
							</p>
						</div>

						{/* Error Message */}
						{error && (
							<div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
								<div className="flex items-start space-x-3">
									<svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
									</svg>
									<p className="text-sm text-red-800 dark:text-red-400">{error}</p>
								</div>
							</div>
						)}

						{/* Success Message */}
						{success && (
							<div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
								<div className="flex items-start space-x-3">
									<svg className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
									</svg>
									<p className="text-sm text-green-800 dark:text-green-400">{success}</p>
								</div>
							</div>
						)}

						<div className="space-y-6">
							{/* Team Name */}
							<div>
								<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
									Team Name <span className="text-red-500">*</span>
								</label>
								<input
									type="text"
									value={teamName}
									onChange={(e) => setTeamName(e.target.value)}
									disabled={!isOwner}
									placeholder="Acme Corp"
									className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-slate-100 dark:disabled:bg-slate-800 disabled:cursor-not-allowed"
								/>
								<p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
									This is the name displayed across the platform
								</p>
							</div>

							{/* Team Role */}
							<div>
								<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
									Your Role
								</label>
								<div className="px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800">
									<span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
										isOwner
											? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
											: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
									}`}>
										{isOwner ? 'Owner' : 'Admin'}
									</span>
								</div>
								<p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
									{isOwner
										? 'You have full control over this team including billing and member management'
										: 'You can manage AI connectors and devices but cannot modify team settings or billing'}
								</p>
							</div>

							{/* License Information */}
							<div>
								<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
									License Status
								</label>
								<div className="px-4 py-3 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800">
									<div className="flex items-center justify-between">
										<div>
											<span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
												currentTeam.license.type === 'active'
													? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
													: currentTeam.license.type === 'trial'
													? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
													: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
											}`}>
												{currentTeam.license.type === 'active'
													? 'Active Subscription'
													: currentTeam.license.type === 'trial'
													? `Trial (${currentTeam.license.days_remaining || 0} days remaining)`
													: 'Expired'}
											</span>
										</div>
										{isOwner && (
											<a
												href="/billing"
												className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
											>
												Manage Billing →
											</a>
										)}
									</div>
									<div className="mt-3 text-sm text-slate-600 dark:text-slate-400">
										<div className="grid grid-cols-2 gap-4">
											<div>
												<span className="font-medium">AI Workspaces:</span> {currentTeam.license.current_agents || 0}/{currentTeam.license.agent_limit}
												<span className="block text-xs text-slate-400 dark:text-slate-500">Protected AI agent instances</span>
											</div>
											<div>
												<span className="font-medium">Team Seats:</span> {currentTeam.license.current_seats || 0}/{currentTeam.license.seat_limit}
												<span className="block text-xs text-slate-400 dark:text-slate-500">Members with console access</span>
											</div>
										</div>
									</div>
								</div>
							</div>

							{/* Save Button */}
							{isOwner && (
								<div className="flex justify-end pt-4">
									<button
										onClick={saveTeamSettings}
										disabled={saving || loading}
										className="px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer disabled:cursor-not-allowed"
									>
										{saving ? 'Saving...' : 'Save Changes'}
									</button>
								</div>
							)}

							{!isOwner && (
								<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
									<div className="flex items-start space-x-3">
										<svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<p className="text-sm text-blue-800 dark:text-blue-400">
											You need to be a team owner to modify these settings. Contact your team owner to request changes.
										</p>
									</div>
								</div>
							)}
						</div>
					</div>

					{/* Team Members Link */}
					<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-6">
						<div className="flex items-center justify-between">
							<div>
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Team Members</h3>
								<p className="text-sm text-slate-600 dark:text-slate-400">
									Manage team members and invitations
								</p>
							</div>
							<a
								href="/team"
								className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium transition-colors"
							>
								Manage Members →
							</a>
						</div>
					</div>
					</>)}

					{activeTab === 'workspace' && (<>
					{/* Workspace Configurations */}
					<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-6">
						<div className="mb-6">
							<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Workspace Configuration</h3>
							<p className="text-sm text-slate-600 dark:text-slate-400">
								Default workspace configuration for all new workspaces. Individual workspaces can override these settings.
							</p>
						</div>

						<div className="space-y-4">
							{/* Policy status messages */}
							{(policyStatus || channelKeyStatus) && (() => {
								const status = policyStatus || channelKeyStatus;
								const clearStatus = () => { setPolicyStatus(null); setChannelKeyStatus(null); };
								return (
									<div className={`rounded-xl p-4 flex items-center justify-between ${
										status!.type === 'success'
											? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
											: 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
									}`}>
										<div className="flex items-center space-x-3">
											{status!.type === 'success' ? (
												<svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
												</svg>
											) : (
												<svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
												</svg>
											)}
											<span className={`text-sm ${
												status!.type === 'success' ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'
											}`}>{status!.message}</span>
										</div>
										<button onClick={clearStatus} className="p-1 rounded-lg cursor-pointer text-slate-400 hover:text-slate-600">
											<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
											</svg>
										</button>
									</div>
								);
							})()}

							{teamPolicyLoading ? (
								<div className="flex items-center justify-center h-32">
									<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
								</div>
							) : teamPolicy ? (
								<>
									<PolicyConfigTabs
										config={teamPolicy}
										onConfigChange={handlePolicyChange}
										onConnectProvider={handleConnectProvider}
										onRemoveProvider={handleRemoveProvider}
										hideGatewayStatus
										channelKeys={channelKeys}
										onAddChannelKey={(channelType) => {
											setEditingKeyId(null);
											setChannelKeyForm({ channel_type: channelType, label: '', is_default: false, config: {} });
											setShowChannelKeyModal(true);
										}}
										onEditChannelKey={(key) => openEditKeyModal(key)}
										onDeleteChannelKey={(keyId) => handleDeleteChannelKey(keyId)}
										teamAgents={teamAgents}
										onAssignChannelKey={handleAssignChannelKey}
									/>

									{/* Save button for policy */}
									{isOwner && (
										<div className="flex items-center justify-end pt-2">
											<button
												onClick={saveTeamPolicy}
												disabled={teamPolicySaving}
												className="px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer disabled:cursor-not-allowed flex items-center space-x-2"
											>
												{teamPolicySaving ? (
													<>
														<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
														<span>Saving...</span>
													</>
												) : (
													<span>Save Configurations</span>
												)}
											</button>
										</div>
									)}

									{!isOwner && (
										<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
											<div className="flex items-start space-x-3">
												<svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
												</svg>
												<p className="text-sm text-blue-800 dark:text-blue-400">
													Only team owners can modify workspace configurations. Contact your team owner to request changes.
												</p>
											</div>
										</div>
									)}
								</>
							) : (
								<div className="text-center py-8 text-slate-500 dark:text-slate-400">
									<p>Unable to load workspace configurations. Please try again later.</p>
								</div>
							)}
						</div>
					</div>

					</>)}

					{activeTab === 'skills' && (<>
					{/* Team Default Skills */}
					<div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6">
						<div className="mb-4">
							<h3 className="text-lg font-semibold text-slate-900 dark:text-white">Default Skills</h3>
							<p className="text-sm text-slate-500 dark:text-slate-400">
								Skills added here are the default for all workspaces — workspaces can override with their own skill list
							</p>
						</div>
						<SkillManager teamMode />
					</div>
					</>)}
				</div>
			</Layout>

			{/* API Key Modal (OpenAI, Google AI, OpenRouter) */}
			{showApiKeyModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
						<div className="p-6 border-b border-slate-200 dark:border-slate-700">
							<div className="flex items-center justify-between">
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white">
									{showApiKeyModal === 'openai' && 'Configure OpenAI API Key'}
									{showApiKeyModal === 'google_ai' && 'Configure Google AI API Key'}
									{showApiKeyModal === 'openrouter' && 'Configure OpenRouter API Key'}
								</h3>
								<button
									onClick={() => setShowApiKeyModal(null)}
									className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors cursor-pointer"
								>
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>
						</div>

						<div className="p-6 space-y-4">
							<div>
								<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
									API Key
								</label>
								<input
									type="password"
									value={apiKeyInput}
									onChange={(e) => setApiKeyInput(e.target.value)}
									placeholder={
										showApiKeyModal === 'openai' ? 'sk-...' :
										showApiKeyModal === 'google_ai' ? 'AIza...' :
										'Enter your API key'
									}
									className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400"
								/>
							</div>

							<div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
								<div className="flex items-start space-x-2">
									<svg className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
									</svg>
									<p className="text-sm text-amber-700 dark:text-amber-300">
										This key will be the team default. Individual workspaces can override it with their own key.
									</p>
								</div>
							</div>
						</div>

						<div className="p-6 bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 flex justify-end space-x-3">
							<button
								onClick={() => setShowApiKeyModal(null)}
								className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors cursor-pointer"
							>
								Cancel
							</button>
							<button
								onClick={handleSaveApiKey}
								disabled={!apiKeyInput.trim() || savingCredential}
								className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-400 text-white rounded-lg font-medium transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center space-x-2"
							>
								{savingCredential ? (
									<>
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
										<span>Saving...</span>
									</>
								) : (
									<span>Save Key</span>
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Claude Token Modal */}
			{showClaudeTokenModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
						<div className="p-6 border-b border-slate-200 dark:border-slate-700">
							<div className="flex items-center justify-between">
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white">
									Connect Claude
								</h3>
								<button
									onClick={() => setShowClaudeTokenModal(false)}
									className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors cursor-pointer"
								>
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>
						</div>

						<div className="p-6 space-y-4">
							{/* Instructions */}
							<div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
								<p className="text-sm font-medium text-slate-900 dark:text-white mb-3">
									Step 1: Run the setup command
								</p>
								<p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
									Open a terminal on your local machine and run:
								</p>
								<div className="bg-slate-900 dark:bg-black rounded-lg p-3 font-mono text-sm text-green-400 flex items-center justify-between">
									<code>claude setup-token</code>
									<button
										onClick={() => navigator.clipboard.writeText('claude setup-token')}
										className="p-1.5 text-slate-400 hover:text-white transition-colors cursor-pointer"
										title="Copy command"
									>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
										</svg>
									</button>
								</div>
								<p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
									Complete the OAuth flow in your browser. The token will be displayed in your terminal.
								</p>
							</div>

							<div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
								<p className="text-sm font-medium text-slate-900 dark:text-white mb-3">
									Step 2: Paste the token
								</p>
								<p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
									Copy the token from your terminal and paste it below:
								</p>
								<input
									type="password"
									value={claudeTokenInput}
									onChange={(e) => setClaudeTokenInput(e.target.value)}
									placeholder="sk-ant-oat01-..."
									className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 font-mono text-sm"
								/>
							</div>

							<div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
								<div className="flex items-start space-x-2">
									<svg className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
									</svg>
									<p className="text-sm text-amber-700 dark:text-amber-300">
										This token will be the team default. Individual workspaces can override it with their own token.
									</p>
								</div>
							</div>
						</div>

						<div className="p-6 bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 flex justify-end space-x-3">
							<button
								onClick={() => setShowClaudeTokenModal(false)}
								className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors cursor-pointer"
							>
								Cancel
							</button>
							<button
								onClick={handleSaveClaudeToken}
								disabled={!claudeTokenInput.trim() || savingCredential}
								className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-slate-400 disabled:to-slate-400 text-white rounded-lg font-medium transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center space-x-2"
							>
								{savingCredential ? (
									<>
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
										<span>Saving...</span>
									</>
								) : (
									<span>Save Token</span>
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Channel Key Modal (Add/Edit) */}
			{showChannelKeyModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
						<div className="p-6 border-b border-slate-200 dark:border-slate-700">
							<div className="flex items-center justify-between">
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white">
									{editingKeyId ? 'Edit Channel Key' : 'Add Channel Key'}
								</h3>
								<button
									onClick={() => { setShowChannelKeyModal(false); setEditingKeyId(null); }}
									className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors cursor-pointer"
								>
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>
						</div>

						<div className="p-6 space-y-4">
							{/* Channel Type (only for new keys) */}
							{!editingKeyId && (
								<div>
									<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
										Channel Type
									</label>
									<select
										value={channelKeyForm.channel_type}
										onChange={(e) => setChannelKeyForm({ ...channelKeyForm, channel_type: e.target.value, config: {} })}
										className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
									>
										{Object.entries(CHANNEL_TYPE_LABELS).map(([value, label]) => (
											<option key={value} value={value}>{label}</option>
										))}
									</select>
								</div>
							)}

							{/* Label */}
							<div>
								<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
									Label
								</label>
								<input
									type="text"
									value={channelKeyForm.label}
									onChange={(e) => setChannelKeyForm({ ...channelKeyForm, label: e.target.value })}
									placeholder="e.g. Support Bot, Dev Bot"
									className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400"
								/>
							</div>

							{/* Dynamic Secret Fields */}
							{(CHANNEL_CONFIG_FIELDS[channelKeyForm.channel_type] || []).map((field) => (
								<div key={field.key}>
									<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
										{field.label} {field.required && <span className="text-red-500">*</span>}
									</label>
									<input
										type="password"
										value={channelKeyForm.config[field.key] || ''}
										onChange={(e) => setChannelKeyForm({
											...channelKeyForm,
											config: { ...channelKeyForm.config, [field.key]: e.target.value },
										})}
										placeholder={editingKeyId ? '(unchanged)' : field.placeholder}
										className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400"
									/>
								</div>
							))}

							{/* Set as Default */}
							<label className="flex items-center space-x-3 cursor-pointer">
								<input
									type="checkbox"
									checked={channelKeyForm.is_default}
									onChange={(e) => setChannelKeyForm({ ...channelKeyForm, is_default: e.target.checked })}
									className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-primary-600"
								/>
								<span className="text-sm text-slate-700 dark:text-slate-300">Set as default for new connectors</span>
							</label>
						</div>

						<div className="p-6 bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 flex justify-end space-x-3">
							<button
								onClick={() => { setShowChannelKeyModal(false); setEditingKeyId(null); }}
								className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors cursor-pointer"
							>
								Cancel
							</button>
							<button
								onClick={handleSaveChannelKey}
								disabled={!channelKeyForm.label.trim() || savingChannelKey}
								className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-400 text-white rounded-lg font-medium transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center space-x-2"
							>
								{savingChannelKey ? (
									<>
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
										<span>Saving...</span>
									</>
								) : (
									<span>{editingKeyId ? 'Update Key' : 'Create Key'}</span>
								)}
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
}
